import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from './ui/Button';

/** Two columns of twenty: one page is a screenful, not a scroll. */
export const COLUMN_SIZE = 20;
export const PAGE_SIZE = COLUMN_SIZE * 2;

/**
 * Pick things out of a long list, a page at a time.
 *
 * Searching answers "where is this one"; this answers "what is there at all",
 * which needs a different shape — pages of a fixed size rather than a list that
 * grows until it is unusable. Accounts and groups both need it, so it is one
 * component: the second copy of a list like this is where the two start to
 * differ for no reason.
 *
 * Selection lives with the caller, because what a selection *opens* differs:
 * for a person it is their editing mask, for a group its column in the rights
 * matrix. Where there is nothing to select - a department's groups are a
 * directory, not a choice - `selectable={false}` drops the ticks and leaves the
 * paging, which is the part that was wanted.
 */
function PagedPicker({
  items,
  getKey,
  renderPrimary,
  renderSecondary,
  renderBadge,
  selectedKeys = [],
  onToggle,
  selectable = true,
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  const pageCount = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);

  const pageItems = useMemo(
    () => items.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE),
    [items, safePage],
  );

  const columns = [
    pageItems.slice(0, COLUMN_SIZE),
    pageItems.slice(COLUMN_SIZE),
  ];

  return (
    <>
      <div className="admin-user-columns">
        {columns.map((column, index) => (
          <div className="admin-user-column" key={index}>
            {column.map((item) => {
              const key = getKey(item);

              return (
                <label
                  className={`admin-user-pick${
                    selectable ? '' : ' admin-user-pick-static'
                  }`}
                  key={key}
                >
                  {selectable && (
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(key)}
                      onChange={() => onToggle(key)}
                    />
                  )}

                  <span className="admin-user-pick-name">
                    {renderPrimary(item)}
                  </span>

                  {renderSecondary && (
                    <span className="admin-user-pick-login">
                      {renderSecondary(item)}
                    </span>
                  )}

                  {renderBadge && renderBadge(item)}
                </label>
              );
            })}
          </div>
        ))}
      </div>

      {pageCount > 1 && (
        <div className="admin-user-pager">
          <Button
            variant="secondary"
            disabled={safePage === 0}
            onClick={() => setPage(safePage - 1)}
          >
            <i className="fa-solid fa-chevron-left" />
            {t('adminPanel.previousPage')}
          </Button>

          <span className="admin-user-pager-position">
            {t('adminPanel.pagePosition', {
              page: safePage + 1,
              pages: pageCount,
            })}
          </span>

          <Button
            variant="secondary"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage(safePage + 1)}
          >
            {t('adminPanel.nextPage')}
            <i className="fa-solid fa-chevron-right" />
          </Button>
        </div>
      )}
    </>
  );
}

export default PagedPicker;
